import React from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import './Nuvei.scss';

import { getClientCapabilities } from '@/lib/utils/browserUtils';

const NUVEI_SDK_SIMPLY_CONNECT = 'https://cdn.safecharge.com/safecharge_resources/v1/checkout/checkout.js';

const Nuvei = ({
    onPaymentResult,
    nuveiConfig,
    lyriaUrl,
    pspId,
    token,
    paymentRequestId,
    sendErrorToLyria = () => { },
    index,
}) => {
    const checkoutContainerRef = React.useRef(null);
    const [isScriptLoaded, setIsScriptLoaded] = React.useState(false);

    const process = React.useCallback(
        async () => {
            return axios({
                method: 'post',
                url: lyriaUrl + '/nuvei/process',
                headers: {
                    'Content-Type': 'application/json',
                },
                data: {
                    client_capabilities: getClientCapabilities(),
                    request_token: token,
                    payment_request_id: paymentRequestId,
                    psp: pspId,
                    data: {},
                },
                validateStatus: (status) => status === 200 || status === 400,
            });
        },
        [lyriaUrl, paymentRequestId, pspId, token],
    );

    const checkFinalStatus = React.useCallback(
        async () => {
            return axios({
                method: 'get',
                url: nuveiConfig.callback_url,
                headers: {
                    'Content-Type': 'application/json',
                    // this is important to force lyria to reply in json
                    'Accept': 'application/json',
                },
            });
        },
        [nuveiConfig],
    );

    React.useEffect(() => {
        if (document.getElementById('nuvei-script')) {
            setIsScriptLoaded(true);
            return;
        }
        const script = document.createElement('script');
        script.src = NUVEI_SDK_SIMPLY_CONNECT;
        script.id = 'nuvei-script';
        script.async = true;
        script.onload = () => {
            setIsScriptLoaded(true);
        };
        document.body.appendChild(script);
        return () => window.document.body.removeChild(script);
    }, []);

    const handleError = React.useCallback((data) => {
        onPaymentResult({ success: false, data: data });
    }, [onPaymentResult]);

    const handleSuccess = React.useCallback(
        (result) => {
            checkFinalStatus().then((res) => {
                if (res.data?.data?.message?.status === 'error') {
                    return handleError({ code: res?.data?.data?.message?.data?.code || 'PSPError', errorDescription: result.errorDescription,
                    });
                }
                onPaymentResult({ success: true, data: res.data?.data });
            })
                .catch((e) => {
                    console.error(e);
                    onPaymentResult({ success: false, data: { code: 'internal' } });
                });
        }, [checkFinalStatus, onPaymentResult, handleError],
    );

    const processNuveiResponse = React.useCallback((result) => {
        if (result.error) {
            return handleError({ code: 'initializationError', error_message: result.error, psp: 'nuvei', pspId: pspId });
        }
        if (result.transactionStatus === 'ERROR') {
            return handleError({ code: 'PSPError', error_message: result.errorDescription || result.error });
        }
        return handleSuccess(result);
    }, [pspId, handleError, handleSuccess]);

    React.useEffect(() => {
        if (isScriptLoaded && checkoutContainerRef.current && nuveiConfig.sessionToken) {
            if (window.checkout) {
                checkoutContainerRef.current.innerHTML = '';

                window.checkout({
                    fullName: nuveiConfig.fullName,
                    email: nuveiConfig.billingAddress?.email,
                    billingAddress: {
                        email: nuveiConfig.billingAddress?.email,
                        country: nuveiConfig.country,
                    },
                    sessionToken: nuveiConfig.sessionToken,
                    env: nuveiConfig.env,
                    merchantSiteId: nuveiConfig.merchantSiteId,
                    showResponseMessage: false,
                    renderTo: `#checkout-${index}`,
                    merchantId: nuveiConfig.merchantId,
                    country: nuveiConfig.country,
                    locale: nuveiConfig.locale,
                    currency: nuveiConfig.currency,
                    amount: nuveiConfig.amount / 100,
                    pmWhitelist: nuveiConfig.pmWhitelist,
                    savePM: nuveiConfig.savePM,
                    threeD: {
                        v2: {
                            notificationURL: nuveiConfig.callback_url,
                        },
                    },
                    prePayment: function () {
                        return new Promise((resolve, reject) => {
                            process().then((res) => {
                                if (res.status === 200) {
                                    resolve();
                                } else {
                                    reject(new Error('Failed to obtain session token'));
                                }
                            })
                                .catch((e) => {
                                    if (e.response?.status === 422 && e.response?.data?.code === 'order_is_cancelled') {
                                        onPaymentResult({ success: false, data: { code: 'order_is_cancelled' } });
                                    }
                                    console.error('Error during Nuvei process call:', e);
                                    reject(e);
                                });
                        });
                    },
                    onResult: function (result) {
                        processNuveiResponse(result);
                    },
                });
            }
        }
    }, [processNuveiResponse, isScriptLoaded, process, sendErrorToLyria, nuveiConfig, index, onPaymentResult]);
    return (
        <div className={nuveiConfig.pmWhitelist?.length === 1 ? 'nuvei-single-payment-method' : ''}>
            <div id={`checkout-${index}`} ref={checkoutContainerRef}></div>
        </div>
    );
};

Nuvei.propTypes = {
    token: PropTypes.string,
    paymentRequestId: PropTypes.string,
    nuveiConfig: PropTypes.shape({
        sessionToken: PropTypes.string,
        merchantId: PropTypes.string,
        merchantSiteId: PropTypes.string,
        env: PropTypes.string,
        callback_url: PropTypes.string,
        fullName: PropTypes.string,
        locale: PropTypes.string,
        currency: PropTypes.string,
        amount: PropTypes.number,
        country: PropTypes.string,
        billingAddress: PropTypes.object,
        pmWhitelist: PropTypes.arrayOf(PropTypes.string),
        savePM: PropTypes.bool,
    }).isRequired,
    pspId: PropTypes.number.isRequired,
    lyriaUrl: PropTypes.string.isRequired,
    onPaymentResult: PropTypes.func.isRequired,
    sendErrorToLyria: PropTypes.func,
    index: PropTypes.number,
};

Nuvei.displayName = 'Nuvei';

export default Nuvei;
